/**
 * Web-Auftragsübersicht W01 laut AV-101 (#6672).
 * Referenz: docs/auftragsverwaltung/AV-101-web-auftragsuebersicht-w01.md
 */

import { BcmOrderStatusType } from '@shared/models/bcm-order-status';
import { TranslationService } from '@core/translation/translation.service';
import {
    InvoicePositionBillingSnapshot,
    isPositionOnOrderCart,
} from '@shared/helpers/order-billing-model.util';

export const ORDER_OVERVIEW_MODEL_VERSION = '1.0.0';

export interface OrderCartPositionPriceLike extends InvoicePositionBillingSnapshot {
    price?: number | null;
    quantity?: number | null;
    totalPrice?: number | null;
}

export interface OrderListKpiSummary {
    totalActive: number;
    open: number;
    inProgress: number;
    waiting: number;
    paused: number;
    readyForBilling: number;
}

export interface OrderListStatusCountRow {
    lastStatus?: string | null;
    count?: number | string | null;
}

export const ORDER_LIST_WAITING_STATUS_TYPES: BcmOrderStatusType[] = [
    BcmOrderStatusType.WaitingForMaterial,
    BcmOrderStatusType.WaitingForCustomer,
    BcmOrderStatusType.WaitingForExternal,
];

export const ORDER_LIST_CLOSED_STATUS_TYPES: BcmOrderStatusType[] = [
    BcmOrderStatusType.Done,
    BcmOrderStatusType.Payed,
    BcmOrderStatusType.Cancelled,
];

export function isOrderBillingReady(status: unknown): boolean {
    return String(status ?? '') === BcmOrderStatusType.ReadyForBilling;
}

export function isOrderListActiveStatus(status: unknown): boolean {
    const normalized = String(status ?? '');

    return !!normalized && !ORDER_LIST_CLOSED_STATUS_TYPES.includes(normalized as BcmOrderStatusType);
}

export function resolveOrderCartPositionLineTotal(position: OrderCartPositionPriceLike): number {
    const totalPrice = Number(position?.totalPrice);

    if (Number.isFinite(totalPrice)) {
        return totalPrice;
    }

    const price = Number(position?.price ?? 0);
    const quantity = Number(position?.quantity ?? 1);

    if (!Number.isFinite(price) || !Number.isFinite(quantity)) {
        return 0;
    }

    return price * quantity;
}

export function sumOrderCartPositionPrices(
    positions: OrderCartPositionPriceLike[] | null | undefined,
    linkedToOrder = true,
): number {
    return (positions || []).reduce((sum, position) => {
        if (!isPositionOnOrderCart(position, linkedToOrder)) {
            return sum;
        }

        return sum + resolveOrderCartPositionLineTotal(position);
    }, 0);
}

export function buildOrderListKpiSummaryFromStatusRows(
    rows: OrderListStatusCountRow[] | null | undefined,
): OrderListKpiSummary {
    const summary: OrderListKpiSummary = {
        totalActive: 0,
        open: 0,
        inProgress: 0,
        waiting: 0,
        paused: 0,
        readyForBilling: 0,
    };

    (rows || []).forEach((row) => {
        const status = String(row?.lastStatus ?? '');
        const count = Number.parseInt(String(row?.count ?? 0), 10) || 0;

        if (!isOrderListActiveStatus(status)) {
            return;
        }

        summary.totalActive += count;

        switch (status) {
            case BcmOrderStatusType.Open:
                summary.open += count;
                break;
            case BcmOrderStatusType.Processing:
                summary.inProgress += count;
                break;
            case BcmOrderStatusType.Paused:
                summary.paused += count;
                break;
            case BcmOrderStatusType.ReadyForBilling:
                summary.readyForBilling += count;
                break;
            default:
                if (ORDER_LIST_WAITING_STATUS_TYPES.includes(status as BcmOrderStatusType)) {
                    summary.waiting += count;
                }
                break;
        }
    });

    return summary;
}

export function formatOrderListBillingReadyLabel(isReady: unknown): string {
    const ready = isOrderBillingReady(isReady) || isReady === true || isReady === 1 || isReady === '1';

    return TranslationService.translate(
        ready ? 'widgets.docksite.overview.billingReady.yes' : 'widgets.docksite.overview.billingReady.no',
        undefined,
        ready ? 'Ja' : 'Nein',
    );
}

export interface OrderListClientCellSource {
    activeClient?: { toString?: () => string } | null;
    client?: { toString?: () => string } | null;
    clientCompany?: { toString?: () => string } | null;
    clientName?: string | null;
}

/** Person/Company mit toString() für die Tabellen-Spalte „Auftraggeber“. */
export function resolveOrderListClientCellValue(
    order: OrderListClientCellSource | null | undefined,
): { toString?: () => string } | string | null {
    if (!order) {
        return null;
    }

    return order.activeClient ?? order.client ?? order.clientCompany ?? order.clientName ?? null;
}
